import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG = "/photos/photo3.jpg";

const MaskedLine = ({ children, delay }) => (
  <span className="hero-line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onApply }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Shellie and her daughter Helena in warm light"
          className="w-full h-full object-cover object-[center_22%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#241E1A]/95 via-[#241E1A]/70 to-[#241E1A]/40 sm:via-[#241E1A]/55 sm:to-[#241E1A]/30" />
      </motion.div>

      <motion.div style={{ y: textY }} className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 pb-20 sm:pb-28 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C69C6D] mb-6"
          data-testid="hero-overline"
        >
          A 6-month private 1:1 partnership · Mothers of children ages 1–5
        </motion.p>

        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.12] text-[#FBF8F3] max-w-4xl font-medium" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Install an abundance mindset</MaskedLine>
          <MaskedLine delay={0.5}>in your child <em className="text-[#C69C6D] not-italic font-light italic">before age 5</em> —</MaskedLine>
          <MaskedLine delay={0.65}>so they never spend years</MaskedLine>
          <MaskedLine delay={0.8}>unlearning scarcity like you did.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#FBF8F3]/80"
          data-testid="hero-subtext"
        >
          You did the inner work. Now let it reach her — through the words you already say
          at the store, at bath time, and at bedtime.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <button
            data-testid="hero-apply-button"
            onClick={onApply}
            className="rounded-full bg-[#8C4A32] text-[#FBF8F3] px-8 py-4 text-base font-semibold tracking-wide transition-all duration-300 hover:bg-[#733B27] hover:scale-[1.03]"
          >
            Apply for Legacy Language Architecture
          </button>
          <p className="text-sm text-[#FBF8F3]/60">
            Led by <span className="font-script text-xl text-[#C69C6D]">Shellie Davis</span>
            <span className="block text-xs tracking-wide">M.S., CCC-SLP · 10 years in child language development · girl mom</span>
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};
